import { useShow, useTranslate } from '@refinedev/core';
import { Show, DateField } from '@refinedev/antd';
import { Typography, Divider, Descriptions, Tag } from 'antd';
import type { RevenuecatEventDetailOut } from '@/api/generated/schemas';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';

const { Title } = Typography;

export const RevenuecatEventShow = () => {
  const t = useTranslate();
  const { query } = useShow<RevenuecatEventDetailOut>({
    resource: 'monetization.revenuecat_events',
  });
  const { data, isLoading } = query;
  const record = data?.data;

  return (
    <Show isLoading={isLoading}>
      <Title level={4}>{t('base.sections.basic_info')}</Title>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.id')}
        >
          {record?.id}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.event_id')}
        >
          <span style={{ fontFamily: 'monospace' }}>{record?.event_id}</span>
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.event_type')}
        >
          {record?.event_type ? <Tag>{record.event_type}</Tag> : '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.app_user_id')}
        >
          {record?.app_user_id ? (
            <RevenueCatCustomerLink
              id={record.app_user_id}
              title={t('monetization.customers.actions.open_in_revenuecat')}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.environment')}
        >
          {record?.environment ? (
            <Tag
              color={
                record.environment === 'PRODUCTION' ? 'success' : 'warning'
              }
            >
              {record.environment}
            </Tag>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.store')}
        >
          {record?.store ? <Tag>{record.store}</Tag> : '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.product_id')}
        >
          {record?.product_id ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.period_type')}
        >
          {record?.period_type ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.transaction_id')}
          span={2}
        >
          {record?.transaction_id ?? '-'}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Title level={4}>{t('base.sections.system_info')}</Title>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.event_timestamp')}
        >
          {record?.event_timestamp ? (
            <DateField value={record.event_timestamp} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.processed_at')}
        >
          {record?.processed_at ? (
            <DateField value={record.processed_at} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_events.fields.created_at')}
          span={2}
        >
          {record?.created_at ? (
            <DateField value={record.created_at} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Title level={4}>
        {t('monetization.revenuecat_events.fields.payload')}
      </Title>
      {record?.payload ? (
        <pre
          style={{
            background: '#f5f5f5',
            padding: '16px',
            borderRadius: '8px',
            maxHeight: '400px',
            overflow: 'auto',
            fontSize: '13px',
            border: '1px solid #d9d9d9',
          }}
        >
          {JSON.stringify(record.payload, null, 2)}
        </pre>
      ) : (
        '-'
      )}
    </Show>
  );
};
